import React from 'react';
import { appVersionsAPI } from '../services/api';
import {
    Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
    Skeleton, Alert, Stack, TextField, Button, Pagination, Chip, Grid,
} from '@mui/material';
import { AddRounded } from '@mui/icons-material';

export default function Settings() {
    const [versions, setVersions] = React.useState([]);
    const [pagination, setPagination] = React.useState({ total: 0, pages: 1 });
    const [page, setPage]         = React.useState(1);
    const [loading, setLoading]   = React.useState(true);
    const [error, setError]       = React.useState('');
    const [notice, setNotice]     = React.useState('');

    const [version, setVersion]           = React.useState('');
    const [downloadUrl, setDownloadUrl]   = React.useState('');
    const [releaseNotes, setReleaseNotes] = React.useState('');
    const [saving, setSaving]             = React.useState(false);
    const [settingCurrent, setSettingCurrent] = React.useState(null);

    const load = React.useCallback(() => {
        setLoading(true);
        setError('');
        appVersionsAPI.list({ page })
            .then((res) => {
                if (res.data.error === '1') throw new Error(res.data.message || 'Failed to load versions');
                setVersions(res.data.versions || []);
                setPagination(res.data.pagination || { total: 0, pages: 1 });
            })
            .catch((err) => setError(err.message || 'Failed to load versions'))
            .finally(() => setLoading(false));
    }, [page]);

    React.useEffect(() => { load(); }, [load]);

    const handleAdd = async () => {
        if (!version.trim()) return;
        setSaving(true);
        setError('');
        setNotice('');
        try {
            await appVersionsAPI.save({ version_name: version.trim(), download_url: downloadUrl.trim(), release_notes: releaseNotes.trim() });
            setNotice(`Version ${version.trim()} saved.`);
            setVersion(''); setDownloadUrl(''); setReleaseNotes('');
            load();
        } catch (err) {
            setError('Failed to save version. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleSetCurrent = async (v) => {
        setSettingCurrent(v._id);
        setError('');
        try {
            await appVersionsAPI.setCurrent(v._id);
            load();
        } catch (err) {
            setError('Failed to set current version. Please try again.');
        } finally {
            setSettingCurrent(null);
        }
    };

    return (
        <Box>
            <Typography variant="h2" sx={{ mb: 0.5 }}>Settings</Typography>
            <Typography variant="subtitle1" sx={{ mb: 3 }}>Desktop app version management</Typography>

            <Paper sx={{ p: 2.5, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Add / update a version</Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={2}>
                        <TextField size="small" fullWidth label="Version" placeholder="e.g. 0.2.11" value={version} onChange={(e) => setVersion(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField size="small" fullWidth label="Download URL" value={downloadUrl} onChange={(e) => setDownloadUrl(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField size="small" fullWidth label="Release notes" value={releaseNotes} onChange={(e) => setReleaseNotes(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <Button fullWidth variant="contained" startIcon={<AddRounded />} disabled={!version.trim() || saving} onClick={handleAdd}>
                            {saving ? 'Saving…' : 'Save'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
            {notice && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setNotice('')}>{notice}</Alert>}

            <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Version</TableCell>
                                <TableCell>Download URL</TableCell>
                                <TableCell>Release Notes</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                [...Array(3)].map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell colSpan={5}>
                                            <Skeleton variant="rectangular" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 1 }} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : versions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">No versions added yet.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                versions.map((v) => (
                                    <TableRow key={v._id} sx={{ '&:last-child td': { border: 0 } }}>
                                        <TableCell>{v.version_name}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {v.download_url || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">{v.release_notes || '—'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            {v.is_current
                                                ? <Chip label="Current" size="small" sx={{ bgcolor: 'rgba(16,185,129,0.15)', color: '#10B981', fontWeight: 600 }} />
                                                : <Chip label="Inactive" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: 'text.secondary' }} />
                                            }
                                        </TableCell>
                                        <TableCell align="right">
                                            {!v.is_current && (
                                                <Button size="small" disabled={settingCurrent === v._id} onClick={() => handleSetCurrent(v)}>
                                                    {settingCurrent === v._id ? 'Setting…' : 'Set Current'}
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Box>
                {pagination.pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        <Pagination count={pagination.pages} page={page} onChange={(_, p) => setPage(p)} color="primary" shape="rounded" />
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
